import type { Timestamp } from "firebase/firestore";
import { bucharestDayIndex } from "./bucharest-day";

export type NotificationTypeId =
  | "RCA_EXPIRATION"
  | "ITP_EXPIRATION"
  | "VIGNETTE_EXPIRATION"
  | "OIL_CHANGE_DUE_DATE"
  | "FIRE_EXTINGUISHER_EXPIRATION"
  | "MEDICAL_KIT_EXPIRATION"
  | "CASCO_INSURANCE_EXPIRATION"
  | "CMR_INSURANCE_EXPIRATION"
  | "SERVICE_DUE_DATE"
  | "TRAVEL_INSURANCE_EXPIRATION"
  | "PAD_INSURANCE_EXPIRATION"
  | "MALPRAXIS_INSURANCE_EXPIRATION"
  | "ROAD_ASSIST_INSURANCE_EXPIRATION"
  | "CUSTOM_NOTIFICATION";

export type NotifTypeMeta = {
  id: NotificationTypeId;
  shortLabel: string;
  longLabel: string;
  icon: string;
  desc: string;
};

// Mirrors notification_title_*_short / titleRes from Android strings.xml.
export const NOTIF_TYPES: Record<NotificationTypeId, NotifTypeMeta> = {
  RCA_EXPIRATION: {
    id: "RCA_EXPIRATION",
    shortLabel: "RCA",
    longLabel: "Asigurare RCA",
    icon: "verified_user",
    desc: "Asigurare obligatorie",
  },
  ITP_EXPIRATION: {
    id: "ITP_EXPIRATION",
    shortLabel: "ITP",
    longLabel: "Inspecția tehnică periodică",
    icon: "rule",
    desc: "Inspecție tehnică",
  },
  VIGNETTE_EXPIRATION: {
    id: "VIGNETTE_EXPIRATION",
    shortLabel: "Rovinietă",
    longLabel: "Rovinietă",
    icon: "qr_code",
    desc: "Taxă de drum",
  },
  CASCO_INSURANCE_EXPIRATION: {
    id: "CASCO_INSURANCE_EXPIRATION",
    shortLabel: "CASCO",
    longLabel: "CASCO",
    icon: "shield",
    desc: "Asigurare facultativă",
  },
  CMR_INSURANCE_EXPIRATION: {
    id: "CMR_INSURANCE_EXPIRATION",
    shortLabel: "CMR",
    longLabel: "CMR",
    icon: "local_shipping",
    desc: "Transport mărfuri",
  },
  SERVICE_DUE_DATE: {
    id: "SERVICE_DUE_DATE",
    shortLabel: "Revizie auto",
    longLabel: "Revizie auto",
    icon: "build",
    desc: "Mentenanță programată",
  },
  OIL_CHANGE_DUE_DATE: {
    id: "OIL_CHANGE_DUE_DATE",
    shortLabel: "Schimb ulei",
    longLabel: "Schimb ulei",
    icon: "local_gas_station",
    desc: "Mentenanță programată",
  },
  FIRE_EXTINGUISHER_EXPIRATION: {
    id: "FIRE_EXTINGUISHER_EXPIRATION",
    shortLabel: "Extinctor auto",
    longLabel: "Extinctor auto",
    icon: "fire_extinguisher",
    desc: "Verificare obligatorie",
  },
  MEDICAL_KIT_EXPIRATION: {
    id: "MEDICAL_KIT_EXPIRATION",
    shortLabel: "Trusă medicală",
    longLabel: "Trusă medicală",
    icon: "medical_services",
    desc: "Termen valabilitate",
  },
  TRAVEL_INSURANCE_EXPIRATION: {
    id: "TRAVEL_INSURANCE_EXPIRATION",
    shortLabel: "Călătorie",
    longLabel: "Asigurare călătorie",
    icon: "flight_takeoff",
    desc: "Valabilă în străinătate",
  },
  PAD_INSURANCE_EXPIRATION: {
    id: "PAD_INSURANCE_EXPIRATION",
    shortLabel: "PAD",
    longLabel: "Asigurare PAD",
    icon: "flood",
    desc: "Obligatorie pentru locuință",
  },
  MALPRAXIS_INSURANCE_EXPIRATION: {
    id: "MALPRAXIS_INSURANCE_EXPIRATION",
    shortLabel: "Malpraxis",
    longLabel: "Asigurare malpraxis",
    icon: "medical_information",
    desc: "Răspundere profesională",
  },
  ROAD_ASSIST_INSURANCE_EXPIRATION: {
    id: "ROAD_ASSIST_INSURANCE_EXPIRATION",
    shortLabel: "Asistență rutieră",
    longLabel: "Asigurare asistență rutieră",
    icon: "car_crash",
    desc: "Asistență pe drum",
  },
  CUSTOM_NOTIFICATION: {
    id: "CUSTOM_NOTIFICATION",
    shortLabel: "Alt tip de alertă",
    longLabel: "Alt tip de alertă",
    icon: "edit_note",
    desc: "Reminder personalizat",
  },
};

// The three insurance types not linked to a car. Mirrors the Android design
// (android/docs/superpowers/specs/2026-06-06-carless-notifications-design.md):
// carless-ness is keyed off the type; backend-written docs omit car fields
// entirely and Android-written ones carry "" blanks — both parse to carId "".
const CARLESS_NOTIF_TYPES: ReadonlySet<NotificationTypeId> = new Set([
  "TRAVEL_INSURANCE_EXPIRATION",
  "PAD_INSURANCE_EXPIRATION",
  "MALPRAXIS_INSURANCE_EXPIRATION",
]);

export function isCarlessNotifType(t: NotificationTypeId): boolean {
  return CARLESS_NOTIF_TYPES.has(t);
}

// Order mirrors the Android type picker (default → less common), carless
// insurance types before CUSTOM. Previously TYPE_ORDER in NotificationDialog.
export const NOTIF_TYPE_ORDER: readonly NotificationTypeId[] = [
  "RCA_EXPIRATION",
  "ROAD_ASSIST_INSURANCE_EXPIRATION",
  "ITP_EXPIRATION",
  "VIGNETTE_EXPIRATION",
  "CASCO_INSURANCE_EXPIRATION",
  "CMR_INSURANCE_EXPIRATION",
  "SERVICE_DUE_DATE",
  "OIL_CHANGE_DUE_DATE",
  "FIRE_EXTINGUISHER_EXPIRATION",
  "MEDICAL_KIT_EXPIRATION",
  "TRAVEL_INSURANCE_EXPIRATION",
  "PAD_INSURANCE_EXPIRATION",
  "MALPRAXIS_INSURANCE_EXPIRATION",
  "CUSTOM_NOTIFICATION",
];

// Which types the create dialog offers, by entry context (spec §3):
// car pinned + allowCarless (generic add on /notificari) → all types;
// car pinned only (car detail page) → car-bound types; no car (0-cars
// user) → carless types only.
export function availableNotifTypes(args: {
  hasCar: boolean;
  allowCarless: boolean;
}): readonly NotificationTypeId[] {
  if (!args.hasCar) return NOTIF_TYPE_ORDER.filter(isCarlessNotifType);
  if (!args.allowCarless)
    return NOTIF_TYPE_ORDER.filter((t) => !isCarlessNotifType(t));
  return NOTIF_TYPE_ORDER;
}

// Mirrors FuelType.serverKey from android/data/src/main/java/asistent/auto/data/types/FuelType.kt
export const FUEL_OPTIONS: { value: string; label: string }[] = [
  { value: "benzina", label: "Benzină" },
  { value: "motorina", label: "Motorină" },
  { value: "electric", label: "Electric" },
  { value: "gpl", label: "GPL" },
  { value: "hibrid benzina", label: "Hibrid benzină" },
  { value: "hibrid diesel", label: "Hibrid diesel" },
  { value: "altul", label: "Altul" },
];

const FUEL_LABELS: Record<string, string> = {
  benzina: "Benzină",
  motorina: "Motorină",
  electric: "Electric",
  gpl: "GPL",
  "benzina si alcool": "Benzină + alcool",
  "hibrid benzina": "Hibrid benzină",
  "hibrid diesel": "Hibrid diesel",
  fara: "—",
  altul: "Altul",
};

// Mirrors CarUseType.value from android/data/src/main/java/asistent/auto/data/types/CarUseType.kt
export const USE_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "personal", label: "Personal" },
  { value: "transport international", label: "Transport internațional" },
  { value: "taxi", label: "Taxi" },
  { value: "inchiriere", label: "Închiriere" },
  { value: "scoala de soferi", label: "Școală de șoferi" },
  { value: "paza si protectie", label: "Pază și protecție" },
  { value: "curierat", label: "Curierat" },
  { value: "constructii", label: "Construcții" },
  { value: "distributie", label: "Distribuție" },
  { value: "transport marfa", label: "Transport marfă" },
  { value: "institutie publica", label: "Instituție publică" },
  { value: "masina de serviciu", label: "Mașină de serviciu" },
  { value: "transport produse farmaceutice", label: "Transport produse farmaceutice" },
];

// Mirrors TipInmatriculare.value from
// android/data/src/main/java/asistent/auto/data/types/TipInmatriculare.kt.
export const REGISTRATION_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "inmatriculat", label: "Înmatriculat" },
  { value: "inregistrat", label: "Înregistrat" },
  { value: "in vederea inmatricularii", label: "În vederea înmatriculării" },
  { value: "in vederea inregistrarii", label: "În vederea înregistrării" },
];

export function formatUseType(value: string | null | undefined): string | null {
  if (!value) return null;
  return USE_TYPE_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

export function fuelLabel(serverKey: string | null | undefined): string | null {
  if (!serverKey) return null;
  return FUEL_LABELS[serverKey] ?? serverKey;
}

export function registrationTypeLabel(
  value: string | null | undefined
): string | null {
  if (!value) return null;
  return REGISTRATION_TYPE_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

export type RawImageData = {
  filePath?: string;
  fileType?: string;
  updatedAt?: Timestamp;
};

export type CarImage = {
  filePath: string;
  fileType?: string;
};

export type CarDoc = {
  id: string;
  registrationNumber?: string | null;
  carName?: string | null;
  brandId?: string | null;
  modelId?: string | null;
  customModel?: string | null;
  categoryId?: string | null;
  buildYear?: number | null;
  vin?: string | null;
  fuel?: string | null;
  kmCount?: number | null;
  enginePower?: number | null;
  capacity?: number | null;
  seatsCount?: number | null;
  maxWeight?: number | null;
  carIdentityCard?: string | null;
  // The talon's "Numărul certificatului"; Android saves it after a vignette lookup finds vignettes.
  registrationCertificateNumber?: string | null;
  useType?: string | null;
  registrationType?: string | null;
  ownerId?: string | null;
  userId?: string | null;
  ownerAddressId?: string | null;
  userAddressId?: string | null;
  firstRegistrationDate?: string | null;
  image?: RawImageData | null;
};

export type Car = CarDoc & {
  brandName?: string | null;
  modelName?: string | null;
  categoryName?: string | null;
  imageUrl?: string | null;
};

// True when the car has the minimum data `legal-getPrecontractInfoV2` requires.
// Backend resolves policyholder as user → owner; either side may satisfy but
// its matching address must be present. See `docs/contracts/rca.md` § 1.
export function isCarRcaReady(
  car: Pick<Car, "ownerId" | "userId" | "ownerAddressId" | "userAddressId">,
): boolean {
  const ownerOk = Boolean(car.ownerId && car.ownerAddressId);
  const userOk = Boolean(car.userId && car.userAddressId);
  return ownerOk || userOk;
}

// Strips whitespace and dashes/underscores from a registration number so storage stays
// canonical (e.g. "B 101 RYA", "b-101-rya", "B101RYA" all save as "B101RYA").
export function cleanRegistrationNumber(s: string | null | undefined): string {
  if (!s) return "";
  return s.replace(/[\s_-]+/g, "").toUpperCase();
}

// Mirrors `String.toServerDate()` from android/data/.../utils/ServerUtils.kt — server stores
// `firstRegistrationDate` as a `yyyy-MM-dd` string. Parse as local-noon to avoid TZ drift.
export function parseFirstRegistrationDate(
  s: string | null | undefined
): Date | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  return new Date(y, mo, d, 12, 0, 0, 0);
}

export type CarNotif = {
  id: string;
  type: NotificationTypeId;
  date: Date | null;
  notificationDate: Date | null;
  carId: string;
  carRegNumber: string;
  carName?: string | null;
  name?: string | null;
  documentId?: string | null;
  kmCount?: number | null;
};

export type RawNotifDoc = {
  id: string;
  type?: string;
  date?: Timestamp;
  notificationDate?: Timestamp;
  carId?: string;
  carRegNumber?: string;
  carName?: string | null;
  name?: string | null;
  documentId?: string | null;
  kmCount?: number | null;
};

export type HeroVariant = "indigo" | "warm" | "graphite";

export function parseNotifType(raw: string | undefined): NotificationTypeId {
  if (raw && raw in NOTIF_TYPES) return raw as NotificationTypeId;
  return "CUSTOM_NOTIFICATION";
}

export function toCarNotif(raw: RawNotifDoc): CarNotif {
  return {
    id: raw.id,
    type: parseNotifType(raw.type),
    date: raw.date ? raw.date.toDate() : null,
    notificationDate: raw.notificationDate ? raw.notificationDate.toDate() : null,
    carId: raw.carId ?? "",
    carRegNumber: raw.carRegNumber ?? "",
    carName: raw.carName ?? null,
    name: raw.name ?? null,
    documentId: raw.documentId ?? null,
    kmCount: raw.kmCount ?? null,
  };
}

// ── Date helpers ────────────────────────────────────────────────
const MONTH_DAYS = 30;
// Android NotificationUtils uses WARNING_TIME = DAY * 29 against day-end.
// daysUntil() counts whole Bucharest calendar days, so the matching threshold is < 29.
const WARN_DAYS = 29;

// Day counts are Romanian calendar days, not the browser's — see lib/bucharest-day.ts.
export function daysUntil(d: Date | null | undefined): number | null {
  if (!d) return null;
  return bucharestDayIndex(d) - bucharestDayIndex(new Date());
}

export type Status = "success" | "warn" | "error" | "neutral";

export function statusFor(d: Date | null | undefined): Status {
  const n = daysUntil(d);
  if (n == null) return "neutral";
  if (n < 0) return "error";
  if (n <= WARN_DAYS) return "warn";
  return "success";
}

export function fmtRelative(d: Date | null | undefined): string {
  const n = daysUntil(d);
  if (n == null) return "—";
  if (n < 0) return "expirat";
  if (n === 0) return "astăzi";
  if (n === 1) return "mâine";
  // Show exact day counts up to (and including) 30 days — matches the
  // original `< 31` boundary the notification surfaces shipped with. The
  // doc-chip text (`expiryShortText`) intentionally rolls into months
  // a day earlier (29 → "29 zile", 30 → "1 lună").
  if (n <= MONTH_DAYS) return `în ${n} zile`;
  // Past a year, roll into years so badges read "5 ani" rather than "61 luni"
  // — same 365-day threshold fmtRelativeLong uses, keeping the two aligned.
  if (n < 365) return `${Math.floor(n / MONTH_DAYS)} luni`;
  const years = Math.floor(n / 365);
  return years === 1 ? "1 an" : `${years} ani`;
}

export function fmtRelativeLong(d: Date | null | undefined): string {
  const n = daysUntil(d);
  if (n == null) return "—";
  if (n < 0) return `expirat de ${Math.abs(n)} zile`;
  if (n === 0) return "expiră astăzi";
  if (n === 1) return "expiră mâine";
  if (n <= MONTH_DAYS) return `expiră în ${n} zile`;
  if (n < 365) return `expiră în ${Math.floor(n / MONTH_DAYS)} luni`;
  const years = Math.floor(n / 365);
  return years === 1 ? "expiră peste un an" : `expiră peste ${years} ani`;
}

const dateFmt = new Intl.DateTimeFormat("ro-RO", {
  day: "2-digit",
  month: "short",
  year: "numeric",
});
const dateLongFmt = new Intl.DateTimeFormat("ro-RO", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

export function fmtDate(d: Date | null | undefined): string {
  if (!d) return "—";
  return dateFmt.format(d);
}
export function fmtDateLong(d: Date | null | undefined): string {
  if (!d) return "—";
  return dateLongFmt.format(d);
}
// `enginePower` is stored in kW; CP = kW × 1.36, rounded — the same "110 kW · 150 CP" Android and
// iOS show under "Date tehnice".
export function fmtEnginePower(kw: number): string {
  return `${kw} kW · ${Math.round(kw * 1.36)} CP`;
}
export function fmtKm(n: number | null | undefined): string {
  if (n == null) return "—";
  return n.toLocaleString("ro-RO") + " km";
}

// Mirrors `expiryShortText` from CarsListUiMapper.kt — used inside doc chips.
export function expiryShortText(d: Date | null | undefined): string {
  const n = daysUntil(d);
  if (n == null) return "Lipsă";
  if (n < 0) return `−${Math.abs(n)} zile`;
  if (n === 1) return "1 zi";
  if (n < MONTH_DAYS) return `${n} zile`;
  const months = Math.floor(n / MONTH_DAYS);
  if (months === 1) return "1 lună";
  return `${months} luni`;
}

// ── Per-car derivations ─────────────────────────────────────────
export function notifsForCar(notifs: CarNotif[], carId: string): CarNotif[] {
  return notifs.filter((n) => n.carId === carId);
}

export type DocChip = {
  id: string;
  type: NotificationTypeId;
  label: string;
  icon: string;
  value: string;
  status: Status;
};

// Mirrors Android's `notificationsList.sortedWith(dateOrder()).map { it.toDocChip(...) }`.
// Renders ALL notifications for the car — no cap, no hardcoded RCA/ITP/Vignette set.
export function notifChips(notifs: CarNotif[], carId: string): DocChip[] {
  return notifsForCar(notifs, carId)
    .slice()
    .sort((a, b) => {
      const at = a.date?.getTime() ?? Number.POSITIVE_INFINITY;
      const bt = b.date?.getTime() ?? Number.POSITIVE_INFINITY;
      return at - bt;
    })
    .map((n) => {
      const meta = NOTIF_TYPES[n.type];
      return {
        id: n.id,
        type: n.type,
        // Mirrors Android's `toDocChip`: custom alerts carry the user's own name
        // ("Card OMV"), so a car with several of them doesn't show a row of
        // identical "Alt tip de alertă" chips.
        label: n.name?.trim() || meta.shortLabel,
        icon: meta.icon,
        value: expiryShortText(n.date),
        status: statusFor(n.date),
      };
    });
}

const TONE_RANK: Record<Status, number> = {
  error: 3,
  warn: 2,
  success: 1,
  neutral: 0,
};

export function carWorstStatus(notifs: CarNotif[], carId: string): Status {
  const ns = notifsForCar(notifs, carId);
  if (ns.length === 0) return "neutral";
  let worst: Status = "success";
  for (const n of ns) {
    const s = statusFor(n.date);
    if (TONE_RANK[s] > TONE_RANK[worst]) worst = s;
  }
  return worst;
}

// Hero accent maps from tone (matches Android's accentForTone).
export function heroForStatus(status: Status): HeroVariant {
  if (status === "warn") return "warm";
  if (status === "error") return "graphite";
  return "indigo"; // success or neutral
}

// ── Headline / action ───────────────────────────────────────────

// The `config/buyFlags.<product>.web` kill-switches, resolved by the caller via
// `useBuyGate(product) === "allowed"`. Every field is optional and treated as
// `false` when absent, so a caller that forgets a product hides its CTA instead
// of re-opening a switched-off one. Mirrors the `getBuyFlags`/`rcaBuyEnabled`
// arguments of Android's `NotificationType.getActionText`.
//
// Rovinieta (VIGNETTE_EXPIRATION) is deliberately absent: it's a road tax, not
// an insurance product, and has no entry in `config/buyFlags`.
export type NotifActionFlags = {
  rca?: boolean;
  roadAssist?: boolean;
  casco?: boolean;
  cmr?: boolean;
  travel?: boolean;
  pad?: boolean;
  malpraxis?: boolean;
};

// Timing/type rule only: could this kind of notification ever expose an inline
// action, and is it close enough to expiry for one to be useful?
//
// Deliberately flag-free — it answers "is this notification worth headlining?",
// which drives `pickHeadlineNotification`/`badgeTextFor`, and the buy flags have
// no say in that: a car whose CASCO expires next week must still say so even
// with CASCO buying switched off. `actionTextFor` layers the flag gate on top
// for the surfaces that actually render a CTA.
function hasActionWindow(notif: CarNotif): boolean {
  const n = daysUntil(notif.date);
  const withinWindow = n == null || n < WARN_DAYS;
  switch (notif.type) {
    // canRenewRca: renewable broadly within ~13 months, so no window here —
    // callers that render the RCA CTA narrow it with `canBuyRcaNow` instead
    // (see `actionableNotifsForCar`'s `rcaGate`).
    case "RCA_EXPIRATION":
    case "ROAD_ASSIST_INSURANCE_EXPIRATION":
      return true;
    // canRenewVignette: expiry null OR within 29 days — the same window the
    // CASCO/CMR offer dialogs use, and the one the carless types (travel, PAD,
    // malpraxis) use for their `/produse?offer=…` deep link.
    case "VIGNETTE_EXPIRATION":
    case "CASCO_INSURANCE_EXPIRATION":
    case "CMR_INSURANCE_EXPIRATION":
    case "TRAVEL_INSURANCE_EXPIRATION":
    case "PAD_INSURANCE_EXPIRATION":
    case "MALPRAXIS_INSURANCE_EXPIRATION":
      return withinWindow;
    default:
      return false;
  }
}

// Mirrors NotificationType.getActionText() — which kinds of notification expose
// an inline action: the right type, timing that makes the action useful, AND
// the product's buy flag switched on.
//
// The flag gate lives here, at the single chokepoint every CTA surface goes
// through, rather than at each render site — that's what kept "Cumpără RCA"
// showing on the cars grid and the notifications list long after RCA buying was
// switched off in prod. `flags` defaults to `{}` (fail-closed): a surface that
// doesn't pass flags gets no buy CTA at all.
export function actionTextFor(
  notif: CarNotif,
  flags: NotifActionFlags = {}
): string | null {
  if (!hasActionWindow(notif)) return null;
  switch (notif.type) {
    case "RCA_EXPIRATION":
      return flags.rca ? "Cumpără RCA" : null;
    case "ROAD_ASSIST_INSURANCE_EXPIRATION":
      return flags.roadAssist ? "Cumpără Asistență rutieră" : null;
    // Rovinieta is a tax, not a product in `config/buyFlags` — never gated.
    case "VIGNETTE_EXPIRATION":
      return "Reînnoiește";
    case "CASCO_INSURANCE_EXPIRATION":
      return flags.casco ? "Cere ofertă" : null;
    case "CMR_INSURANCE_EXPIRATION":
      return flags.cmr ? "Cere ofertă" : null;
    case "TRAVEL_INSURANCE_EXPIRATION":
      return flags.travel ? "Cere ofertă" : null;
    case "PAD_INSURANCE_EXPIRATION":
      return flags.pad ? "Cere ofertă" : null;
    case "MALPRAXIS_INSURANCE_EXPIRATION":
      return flags.malpraxis ? "Cere ofertă" : null;
    default:
      return null;
  }
}

const ACTIONABLE_TYPE_PRIORITY: Record<NotificationTypeId, number> = {
  RCA_EXPIRATION: 0,
  ROAD_ASSIST_INSURANCE_EXPIRATION: 1,
  CMR_INSURANCE_EXPIRATION: 1,
  CASCO_INSURANCE_EXPIRATION: 2,
  VIGNETTE_EXPIRATION: 3,
  ITP_EXPIRATION: 3,
  OIL_CHANGE_DUE_DATE: 3,
  FIRE_EXTINGUISHER_EXPIRATION: 3,
  MEDICAL_KIT_EXPIRATION: 3,
  SERVICE_DUE_DATE: 3,
  TRAVEL_INSURANCE_EXPIRATION: 3,
  PAD_INSURANCE_EXPIRATION: 3,
  MALPRAXIS_INSURANCE_EXPIRATION: 3,
  CUSTOM_NOTIFICATION: 3,
};

function severityCompare(a: CarNotif, b: CarNotif): number {
  const sa = TONE_RANK[statusFor(a.date)];
  const sb = TONE_RANK[statusFor(b.date)];
  if (sa !== sb) return sb - sa; // worst first
  const ta = a.date?.getTime() ?? Number.POSITIVE_INFINITY;
  const tb = b.date?.getTime() ?? Number.POSITIVE_INFINITY;
  return ta - tb; // earliest first
}

// Mirrors `pickHeadlineNotification` from CarsListUiMapper.kt.
export function pickHeadlineNotification(
  notifs: CarNotif[],
  carId: string
): CarNotif | null {
  const ns = notifsForCar(notifs, carId);
  if (ns.length === 0) return null;
  const actionable = ns.filter((n) => hasActionWindow(n));
  const pool = actionable.length > 0 ? actionable : ns;
  const sorted = pool.slice().sort((a, b) => {
    if (actionable.length > 0) {
      const pa = ACTIONABLE_TYPE_PRIORITY[a.type];
      const pb = ACTIONABLE_TYPE_PRIORITY[b.type];
      if (pa !== pb) return pa - pb;
    }
    return severityCompare(a, b);
  });
  return sorted[0] ?? null;
}

// All actionable notifications for a car, ordered by ACTIONABLE_TYPE_PRIORITY
// (RCA, CMR, CASCO, then everything else). Filtered to the same warn/expired
// window the headline banner uses, so cars with healthy docs months out don't
// surface a noisy CTA.
//
// Gated by the product buy flags (via `actionTextFor`), so a switched-off
// product drops out of the actionable set entirely and the caller falls back to
// its plain headline banner — the expiry warning still reaches the user, only
// the CTA disappears. `flags` defaults to `{}` (fail-closed).
//
// RCA is special: "can I buy RCA now?" is a broker-purchasability question, not
// the generic 29-day notification window. Callers inject that decision via
// `rcaGate` (the `canBuyRcaNow` predicate from `lib/rca-offers`) so this module
// stays free of the offer/Firebase layer. When no gate is supplied, RCA falls
// back to the same `< WARN_DAYS` window as every other actionable type.
export function actionableNotifsForCar(
  notifs: CarNotif[],
  carId: string,
  flags: NotifActionFlags = {},
  rcaGate?: (notif: CarNotif) => boolean
): CarNotif[] {
  return notifsForCar(notifs, carId)
    .filter((n) => {
      if (actionTextFor(n, flags) === null) return false;
      if (n.type === "RCA_EXPIRATION" && rcaGate) return rcaGate(n);
      const days = daysUntil(n.date);
      return days == null || days < WARN_DAYS;
    })
    .sort((a, b) => {
      const pa = ACTIONABLE_TYPE_PRIORITY[a.type];
      const pb = ACTIONABLE_TYPE_PRIORITY[b.type];
      if (pa !== pb) return pa - pb;
      return severityCompare(a, b);
    });
}

// Specific button label for a quick action, including the document type so
// users can tell three buttons apart at a glance ("Cumpără RCA", "Ofertă
// CASCO", "Ofertă CMR", "Reînnoiește rovinieta"). Falls back to the generic
// `actionTextFor` label for any future actionable type.
export function quickActionLabelFor(
  notif: CarNotif,
  flags: NotifActionFlags = {}
): string | null {
  const action = actionTextFor(notif, flags);
  if (!action) return null;
  switch (notif.type) {
    case "RCA_EXPIRATION":
      return "Cumpără RCA";
    case "ROAD_ASSIST_INSURANCE_EXPIRATION":
      return "Cumpără Asistență rutieră";
    case "VIGNETTE_EXPIRATION":
      return "Reînnoiește rovinieta";
    case "CASCO_INSURANCE_EXPIRATION":
      return "Ofertă CASCO";
    case "CMR_INSURANCE_EXPIRATION":
      return "Ofertă CMR";
    default:
      return action;
  }
}

// Banner text. Only shown if the notif is within the warn window or expired.
export function headlineFor(notif: CarNotif | null): string | null {
  if (!notif) return null;
  const n = daysUntil(notif.date);
  if (n == null) return null;
  const label = NOTIF_TYPES[notif.type].shortLabel;
  if (n < 0) return `${label} expirat de ${Math.abs(n)} zile`;
  if (n < MONTH_DAYS) return `${label} expiră în ${n} zile`;
  return null;
}

// Status-pill text for the hero band.
export function badgeText(status: Status): string {
  if (status === "success") return "În regulă";
  if (status === "warn") return "Expiră curând";
  if (status === "error") return "Expirat";
  return "Fără notificări";
}

// Concrete pill text that mentions the worst notification's type, e.g.
// "RCA expirat", "Rovinietă expiră curând". Falls back to the generic
// `badgeText(status)` when there is no headline notification or when a more
// specific label would be misleading. Acronyms and short labels follow the
// existing headline convention (`headlineFor`) which uses masculine "expirat"
// for all types.
export function badgeTextFor(
  notifications: CarNotif[],
  carId: string
): string {
  const status = carWorstStatus(notifications, carId);
  if (status !== "error" && status !== "warn") return badgeText(status);
  const headline = pickHeadlineNotification(notifications, carId);
  if (!headline) return badgeText(status);
  const label = NOTIF_TYPES[headline.type].shortLabel;
  if (status === "error") return `${label} expirat`;
  return `${label} expiră curând`;
}

// ── Display helpers ─────────────────────────────────────────────
// Mirrors Android `CarsListUiMapper.cardTitle` (and `Car.getDisplayValue`):
// the user-set nickname is the primary identity for a car — it's the label
// they actually use to tell their cars apart, so it wins over the structured
// brand+model fields. Only when there's no nickname do we fall through the
// brand+model → plate → placeholder chain.
export function carDisplayName(car: Car): string {
  const nickname = car.carName?.trim() || null;
  if (nickname) return nickname;
  const brand = car.brandName?.trim() || null;
  const model = car.modelName?.trim() || car.customModel?.trim() || null;
  if (brand && model) return `${brand} ${model}`;
  if (brand) return brand;
  if (model) return model;
  if (car.registrationNumber?.trim()) return car.registrationNumber;
  return "Mașină nouă";
}

// Brand + (resolved or custom) model joined with a space, or just whichever
// is present. Returns null when neither is set — letting callers decide what
// to render in that slot (vs. `carDisplayName` which always returns *something*
// by falling back through plate → "Mașină nouă"). Used by surfaces that show
// brand+model as a quieter subtitle below the nickname headline.
export function carBrandModel(car: Car): string | null {
  const brand = car.brandName?.trim() || null;
  const model = car.modelName?.trim() || car.customModel?.trim() || null;
  if (brand && model) return `${brand} ${model}`;
  if (brand) return brand;
  if (model) return model;
  return null;
}

// Subtitle: optional brand+model (only when a nickname is the headline AND
// both brand and model are present — partial brand-only is omitted to match
// Android, which drops one-sided labels rather than render them) · year · km.
// Matches Android `CarsListUiMapper.cardSubtitle` (fuel intentionally not included).
export function carSubtitle(car: Car): string {
  const parts: string[] = [];
  const hasNickname = !!car.carName?.trim();
  if (hasNickname) {
    const brand = car.brandName?.trim() || null;
    const model = car.modelName?.trim() || car.customModel?.trim() || null;
    if (brand && model) parts.push(`${brand} ${model}`);
  }
  if (car.buildYear) parts.push(String(car.buildYear));
  if (car.kmCount != null && car.kmCount > 0) parts.push(fmtKm(car.kmCount));
  return parts.join(" · ");
}
